import { Typography } from '@amway/react-components';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useAuditLogs } from '../../resources/audit-log/useAuditLogs';
import { AuditLogList } from './audit-log-list';
import '../execution-history/index.scss';

const KNOWN_TABLES = [
  { value: '', label: 'All Tables' },
  { value: 'translations', label: 'Translations' },
  { value: 'allow_list_rule', label: 'Allow List Rules' },
  { value: 'cache_control', label: 'Cache Control' }
];

const OPERATIONS = [
  { value: '', label: 'All Operations' },
  { value: 'INSERT', label: 'INSERT' },
  { value: 'UPDATE', label: 'UPDATE' },
  { value: 'DELETE', label: 'DELETE' }
];

const AuditLogScreen: React.FC = () => {
  const { fetchAuditLogs, auditLogs } = useAuditLogs();

  const [currentPage, setCurrentPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [filters, setFilters] = useState({
    tableName: '',
    searchQuery: '',
    operation: ''
  });

  const performFetch = useCallback((offset: number, fetchLimit: number, currentFilters: any) => {
    fetchAuditLogs({
      page: { limit: fetchLimit, offset: offset },
      tableName: currentFilters.tableName || undefined,
      searchQuery: currentFilters.searchQuery || undefined,
      operation: currentFilters.operation || undefined
    });
  }, [fetchAuditLogs]);

  useEffect(() => {
    performFetch(0, 10, filters);
  }, []);

  const handleSearch = () => {
    setCurrentPage(0);
    performFetch(0, limit, filters);
  };

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setCurrentPage(0);

    performFetch(0, limit, newFilters);
  };

  const handleLoadPage = useCallback(async (page: any) => {
    let nextLimit = limit;
    let nextOffset = page.offset !== undefined ? page.offset : 0;

    if (page.limit && page.limit !== limit) {
      nextLimit = page.limit;
      setLimit(nextLimit);
      nextOffset = 0;
      setCurrentPage(0);
    }
    else if (page.offset !== undefined) {
      setCurrentPage(nextOffset / nextLimit);
    }
    performFetch(nextOffset, nextLimit, filters);

  }, [limit, filters, performFetch]);

  return (
    <Container className="audit-logs">
      <Card>
        <Card.Body>
          <Row className="mb-4">
            <Col>
              <Typography variant="heading">Audit Logs</Typography>
              <Typography weight="bold" color="text-gray" className="mt-2">
                View and filter manual changes made to sensitive system tables.
              </Typography>
            </Col>
          </Row>

          <Row className="mb-4 align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label><strong>Table Name</strong></Form.Label>
                <Form.Select
                  value={filters.tableName}
                  onChange={(e) => setFilters({ ...filters, tableName: e.target.value })}
                >
                  {KNOWN_TABLES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label><strong>Operation</strong></Form.Label>
                <Form.Select
                  value={filters.operation}
                  onChange={(e) => setFilters({ ...filters, operation: e.target.value })}
                >
                  {OPERATIONS.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={5}>
              <Form.Group>
                <Form.Label><strong>Search</strong></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="User, ID or content..."
                  value={filters.searchQuery}
                  onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Button
                variant="primary"
                onClick={handleSearch}
                className="w-100"
                disabled={auditLogs.loading}
              >
                {auditLogs.loading ? 'Searching...' : 'SEARCH'}
              </Button>
            </Col>
          </Row>

          <hr />

          <Row>
            <Col>
              {auditLogs.error && (
                <div className="alert alert-danger">
                  Error loading data.
                </div>
              )}

              <AuditLogList
                rows={auditLogs.loading ? null : (auditLogs.data?.content || [])}
                total={auditLogs.data?.total ?? 0}
                loading={auditLogs.loading}
                selectedPage={currentPage}
                setSelectedPage={(page) => {
                  setCurrentPage(page);
                }}
                loadPage={handleLoadPage}
                limit={limit}
              />
            </Col>
          </Row>

        </Card.Body>
      </Card>
    </Container>
  );
};

export default AuditLogScreen;